import { z } from 'zod';
import { ElementIdSchema, ElementTypeEnum } from './element.schema';
import { editorEnvelope as envelope } from './theme-editor-channel.schema';

// The drag half of the builder's conversation with its preview: what is being carried and where it may
// land, where the pointer is when the preview cannot see it, and where the merchant let go.

/**
 * Sent while a merchant is dragging, so the preview can show where it would land. `elementType` is what
 * is being carried out of the library, and is null when the drag ends, dropped or abandoned.
 */
export const EditorDraggingSchema = z.object({
  ...envelope('builder', 'dragging'),
  elementType: ElementTypeEnum.nullable(),
  /** The id of a node already on the page being dragged somewhere new, rather than a new one arriving. */
  movingId: ElementIdSchema.nullable(),
  /**
   * Which containers will take it, and where inside each. A tree has a list per node, so "where may this
   * land" is a list per container rather than one list of indexes. A null parent is the page's own roots.
   */
  allowedParents: z.array(z.object({
    parentId: ElementIdSchema.nullable(),
    indexes: z.array(z.number().int().min(0).max(200)).max(201).optional()
  }).strict()).max(500).optional(),
  /**
   * Where the pointer is, in the preview's own coordinates. Only sent when the builder's drag library
   * has captured the pointer and the frame can no longer see it move for itself.
   */
  pointerY: z.number().optional()
}).strict();

/**
 * Sent when the merchant lets go over the page: put it before the node at this position.
 *
 * The position is optional, and its absence is the message's whole point. The page resolves a boundary
 * from where the pointer is, and sometimes there is none — over background, between two bands, or in the
 * first frames of a fast drag. This used to post nothing at all in that case, and the builder cannot see
 * a release inside a cross-origin frame for itself, so the gesture stayed open: the ghost hung, the card
 * never landed, and the panels stopped answering clicks until the pointer wandered back over them. Saying
 * "let go, nowhere in particular" ends the gesture at once and lets the builder place it as a click would.
 */
export const EditorDroppedSchema = z.object({
  ...envelope('storefront', 'dropped'),
  index: z.number().int().min(0).max(200).optional(),
  /** The container it was let go inside; null is the page's own roots. */
  parentId: ElementIdSchema.nullable().optional()
}).strict();

/**
 * Sent on every pointer move over the page while a drag the builder began is in flight, in the page's
 * own viewport pixels, and once with nulls when the pointer leaves the page. The builder relays this
 * because the browser gives the page nothing else: a mouse pressed in one frame is captured by that
 * frame until it is released, so a drag begun on a library card raises no move inside the preview
 * however long it hovers there. Without it the page drew its landing line wherever the pointer last
 * was, which was nowhere, and the release fell through to the click's own plan.
 */
export const EditorPointerSchema = z.object({
  ...envelope('builder', 'pointer'),
  x: z.number().finite().nullable(),
  y: z.number().finite().nullable()
}).strict();

/**
 * Sent when the merchant lets go over the page, which the builder saw and the page — for the reason
 * above — did not. The page answers with `dropped`, naming the boundary its landing line was on.
 */
export const EditorReleaseSchema = z.object({
  ...envelope('builder', 'release')
}).strict();

/**
 * Sent when a merchant picks a node up on the page itself, by the grip on its toolbar, and carries it
 * past the few pixels that tell a drag from a click. The builder answers with `dragging`, naming where
 * the node may go, and the page then draws its own landing lines under its own pointer.
 */
export const EditorDragStartSchema = z.object({
  ...envelope('storefront', 'dragStart'),
  elementId: ElementIdSchema
}).strict();

/** Sent when a drag begun on the page is abandoned — Escape, or a release before the builder answered. */
export const EditorDragCancelSchema = z.object({
  ...envelope('storefront', 'dragCancel')
}).strict();
